import React, { useEffect, useState } from "react";
import { subDays } from "date-fns";
import { Download, FileText } from "lucide-react";
import { PageHeader } from "../../components/domain/Bits";
import { api, toastError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const day = (value: Date) => value.toISOString().slice(0, 10);
const statuses = ["", "referred", "contacted", "qualified", "converted", "closed_lost"];

export default function ReferralStatusReport() {
  const [fromDate, setFromDate] = useState(day(subDays(new Date(), 29)));
  const [toDate, setToDate] = useState(day(new Date()));
  const [status, setStatus] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [organizations, setOrganizations] = useState<Array<{ id: string; name: string }>>([]);
  const [doctors, setDoctors] = useState<Array<{ id: string; first_name: string; last_name?: string }>>([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/crm/organizations"),
      api.get("/crm/contacts"),
    ]).then(([orgs, contacts]) => {
      setOrganizations(orgs.data || []);
      setDoctors(contacts.data || []);
    }).catch(toastError);
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const response = await api.get("/crm/referrals/status-report.pdf", {
        params: {
          from_date: fromDate, to_date: toDate, status: status || undefined,
          organization_id: organizationId || undefined, doctor_id: doctorId || undefined,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `referral-status-${fromDate}-to-${toDate}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(error);
    } finally {
      setDownloading(false);
    }
  };

  return <>
    <PageHeader title="Doctor Referral Status Report" subtitle="Representative → Clinic → Doctor → Referrals with current status" />
    <main className="mx-auto max-w-5xl space-y-5 p-6 md:p-8" data-testid="referral-status-report">
      <section className="rounded-lg border bg-white p-5">
        <div className="flex items-start gap-3">
          <FileText className="mt-1 h-5 w-5 text-sky-700" />
          <div>
            <h2 className="font-semibold">Doctor-safe PDF</h2>
            <p className="mt-1 text-sm text-slate-600">Representatives receive only their attributed referrals. Internal notes, profiling, commissions, mileage, expenses, and operational remarks never enter this PDF.</p>
          </div>
        </div>
      </section>
      <section className="grid gap-4 rounded-lg border bg-white p-5 md:grid-cols-2">
        <label className="text-sm font-medium">From<Input className="mt-1" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} /></label>
        <label className="text-sm font-medium">To<Input className="mt-1" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} /></label>
        <label className="text-sm font-medium">Clinic
          <select className="mt-1 h-10 w-full rounded-md border px-3 text-sm" value={organizationId} onChange={(event) => setOrganizationId(event.target.value)}>
            <option value="">All authorized clinics</option>
            {organizations.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </label>
        <label className="text-sm font-medium">Doctor
          <select className="mt-1 h-10 w-full rounded-md border px-3 text-sm" value={doctorId} onChange={(event) => setDoctorId(event.target.value)}>
            <option value="">All authorized doctors</option>
            {doctors.map((row) => <option key={row.id} value={row.id}>{row.first_name} {row.last_name || ""}</option>)}
          </select>
        </label>
        <label className="text-sm font-medium">Referral status
          <select className="mt-1 h-10 w-full rounded-md border px-3 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
            {statuses.map((value) => <option key={value || "all"} value={value}>{value ? value.replaceAll("_", " ") : "All statuses"}</option>)}
          </select>
        </label>
        <div className="flex items-end"><Button className="w-full md:w-auto" onClick={download} disabled={downloading || !fromDate || !toDate}><Download className="mr-2 h-4 w-4" />{downloading ? "Preparing PDF…" : "Download Referral Status PDF"}</Button></div>
      </section>
    </main>
  </>;
}
